import type { TextUIPart } from 'ai';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { toast } from 'sonner';

import type { GetAssistantSessionsBySessionIdMessages200MessagesItem as ChatMessage } from '@/api/generated/lucioAPI.schemas';
import { displayEmail } from '@/config/office/office';
import { ChatMessages } from '@/features/assistant/components/ChatMessages';
import { EmailContextDialog } from '@/features/assistant/components/email-context-dialog';
import { Greeting } from '@/features/assistant/components/Greeting';
import { type Suggestion } from '@/features/assistant/components/SuggestionCards';
import { ThinkingIndicator } from '@/features/assistant/components/ThinkingIndicator';
import { useUserInfo } from '@/utils/hooks/useUserInfo';
import { useOfficeContextStore } from '@/utils/stores/useOfficeContextStore';

import {
  type AssistantUIMessage,
  type MessageDataPart,
  type MessageMetadata,
} from '../hooks/useChat';
import { useSessionHistory } from '../hooks/useSessionHistory';
import { currentActivityStep, extractActivitySteps } from '../utils/activitySteps';
import {
  detectLiveContentType,
  extractDraftFromLive,
  extractRevisionFromLive,
} from '../utils/extractDraftFromMessage';
import { ActivityTrail } from './ActivityTrail';
import { SmartSuggestions } from './SmartSuggestions';
import { ThinkingBanner } from './ThinkingBanner';

// ─── Converters ───────────────────────────────────────────────────────────────

function uiMessageToChatMessage(msg: AssistantUIMessage): ChatMessage {
  const text = msg.parts
    .filter((p): p is TextUIPart => p.type === 'text')
    .map((p) => p.text)
    .join('');

  const dataPart = msg.parts.find((p) => p.type === 'data-message-meta') as
    | { data: MessageDataPart; type: string }
    | undefined;

  const meta = msg.metadata as MessageMetadata | undefined;

  // For assistant messages, prefer tool-call inputs over text — drafts and revisions
  // arrive as `tool-draftEmail` / `tool-reviseEmail` parts during streaming.
  const liveContentType = msg.role === 'assistant' ? detectLiveContentType(msg) : 'text';

  // Prefer the post-processed content from data-message-meta (citation markers
  // sequentialized, trailer JSON stripped) once the stream has completed.
  // Falls back to raw streamed text mid-stream.
  let content = dataPart?.data?.content ?? text;
  let contentType: 'text' | 'email_draft' | 'revision' = dataPart?.data?.contentType ?? 'text';
  let originalContent: string | undefined;

  if (liveContentType === 'email_draft') {
    const draft = extractDraftFromLive(msg);
    if (draft) {
      content = JSON.stringify(draft);
      contentType = 'email_draft';
    }
  } else if (liveContentType === 'revision') {
    const rev = extractRevisionFromLive(msg);
    if (rev) {
      content = JSON.stringify(rev.revised);
      originalContent = JSON.stringify(rev.original);
      contentType = 'revision';
    }
  }

  return {
    ...(dataPart?.data?.citations !== undefined && { citations: dataPart.data.citations }),
    content,
    contentType,
    createdAt: new Date().toISOString(),
    id: meta?.assistantMessageId ?? msg.id,
    ...(originalContent !== undefined && { originalContent }),
    role: msg.role === 'user' ? 'USER' : 'ASSISTANT',
    sessionV2Id: meta?.sessionId ?? '',
    streamed: msg.role === 'assistant',
  };
}

// ─── Merge ────────────────────────────────────────────────────────────────────

function mergeMessages(
  serverMessages: ChatMessage[] | undefined,
  localMessages: Array<ChatMessage>,
): Array<ChatMessage> {
  if (!serverMessages) {
    return localMessages;
  }
  if (localMessages.length === 0) {
    return serverMessages;
  }

  const serverById = new Map(serverMessages.map((m) => [m.id, m]));
  const localById = new Map(localMessages.map((m) => [m.id, m]));

  const merged = serverMessages.map((serverMsg) => {
    const localMsg = localById.get(serverMsg.id);
    if (!localMsg) {
      return serverMsg;
    }
    if (serverMsg.contentType === 'revision' || serverMsg.contentType === 'email_draft') {
      return serverMsg;
    }
    const serverLen = serverMsg.content?.length ?? 0;
    const localLen = localMsg.content?.length ?? 0;
    if (localLen > serverLen) {
      return { ...serverMsg, ...localMsg, content: localMsg.content };
    }
    return serverMsg;
  });

  const oldestServerDate =
    serverMessages.length > 0
      ? new Date(
          serverMessages.reduce((oldest, m) =>
            new Date(m.createdAt).getTime() < new Date(oldest.createdAt).getTime() ? m : oldest,
          ).createdAt,
        ).getTime()
      : null;

  const orphaned = localMessages.filter((m) => {
    if (serverById.has(m.id)) {
      return false;
    }
    if (oldestServerDate !== null) {
      const d = new Date(m.createdAt).getTime();
      if (d < oldestServerDate && !m.streamed) {
        return false;
      }
    }
    if (m.role === 'USER' && m.content?.length > 0) {
      return !serverMessages.some(
        (s) => s.role === 'USER' && s.content === m.content && s.contentType === m.contentType,
      );
    }
    if (m.role === 'ASSISTANT' && (m.streamed || m.content?.length > 0)) {
      return true;
    }
    return false;
  });

  return orphaned.length === 0 ? merged : [...merged, ...orphaned];
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AssistantMainContentProps {
  isLoading: boolean;
  isStreaming: boolean;
  messages: Array<AssistantUIMessage>;
  onAttachmentsLoaded: (
    attachments: Array<{
      fileName: string;
      fileSize?: number;
      id: string;
      mimeType?: string;
      url?: string;
    }>,
  ) => void;
  onContextOpenChange: (open: boolean) => void;
  onSuggestionSelect: (suggestion: Suggestion) => void;
  showContextDialog: boolean;
}

export function AssistantMainContent({
  isLoading,
  isStreaming,
  messages,
  onAttachmentsLoaded,
  onContextOpenChange,
  onSuggestionSelect,
  showContextDialog,
}: AssistantMainContentProps) {
  const { t } = useTranslation();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const location = useLocation();
  const userInfo = useUserInfo();
  const containerRef = useRef<HTMLDivElement>(null);

  const contextInfo = useOfficeContextStore((s) => s.contextInfo);
  const contextEmailIds = useOfficeContextStore((s) => s.contextEmailIds);
  const setSelectedCustomEmails = useOfficeContextStore((s) => s.setSelectedCustomEmails);
  const setSessionContext = useOfficeContextStore((s) => s.setSessionContext);
  const setContextEmailIds = useOfficeContextStore((s) => s.setContextEmailIds);
  const setSelectedFolder = useOfficeContextStore((s) => s.setSelectedFolder);

  const sessionHistoryQuery = useSessionHistory({
    enabled: !!urlSessionId,
    forceRefetchOnMount:
      (location.state as { forceSessionHistoryRefetch?: boolean } | null)
        ?.forceSessionHistoryRefetch === true,
    sessionId: urlSessionId ?? '',
  });

  useEffect(() => {
    if (sessionHistoryQuery.isSuccess && sessionHistoryQuery.data) {
      if (isLoading || isStreaming) {
        return;
      }

      const allPages = sessionHistoryQuery.data.pages;
      const firstPage = allPages[0];
      if (!firstPage) {
        return;
      }

      setSessionContext({
        context: firstPage?.context ?? '',
        conversationId: firstPage?.conversationId ?? '',
        draftId: firstPage?.draftId ?? '',
        emailId: firstPage?.emailId ?? '',
      });

      const historyContextEmailIds = firstPage?.contextEmailIds ?? [];
      const historyContext = firstPage?.context;
      const historyMatchesOpenContext =
        historyContext === 'CUSTOM' ||
        historyContext === 'INBOX' ||
        (historyContext === 'THREAD' &&
          firstPage?.conversationId != null &&
          firstPage.conversationId === contextInfo.conversationId) ||
        (historyContext === 'EMAIL' &&
          ((firstPage?.emailId != null && firstPage.emailId === contextInfo.itemId) ||
            (firstPage?.draftId != null && firstPage.draftId === contextInfo.itemId)));

      setSelectedCustomEmails(
        historyContext === 'CUSTOM' || !historyMatchesOpenContext ? historyContextEmailIds : [],
      );
      setContextEmailIds(firstPage?.contextEmailIds ?? []);

      if (historyContext === 'FOLDER') {
        setSelectedFolder(firstPage?.folderId ?? null, firstPage?.folderName ?? null);
      } else {
        setSelectedFolder(null, null);
      }

      const apiAttachments = firstPage?.attachments ?? [];
      if (apiAttachments.length > 0) {
        onAttachmentsLoaded(apiAttachments);
      }
    } else if (!sessionHistoryQuery.isLoading) {
      setSessionContext({ context: '', conversationId: '', emailId: '' });
      setSelectedCustomEmails([]);
      setContextEmailIds([]);
      setSelectedFolder(null, null);
    }
  }, [
    isLoading,
    isStreaming,
    onAttachmentsLoaded,
    sessionHistoryQuery.data,
    sessionHistoryQuery.isSuccess,
    sessionHistoryQuery.isLoading,
    setSessionContext,
    setSelectedCustomEmails,
    setContextEmailIds,
    setSelectedFolder,
    contextInfo.conversationId,
    contextInfo.itemId,
  ]);

  useEffect(() => {
    if (sessionHistoryQuery.isError) {
      console.error('Failed to load session history:', sessionHistoryQuery.error);
    }
  }, [sessionHistoryQuery.isError, sessionHistoryQuery.error]);

  const historyMessages = sessionHistoryQuery.data?.pages
    ? [...sessionHistoryQuery.data.pages].reverse().flatMap((p) => p?.messages ?? [])
    : [];

  const liveMessages = messages.map(uiMessageToChatMessage);

  const allMessages =
    urlSessionId && historyMessages.length > 0
      ? mergeMessages(historyMessages, liveMessages)
      : liveMessages;

  const { isFetching: isHistoryLoading } = sessionHistoryQuery;
  const showWelcome = allMessages.length === 0;

  const activeSessionContext = sessionHistoryQuery.data?.pages?.[0]
    ? {
        context: sessionHistoryQuery.data.pages[0].context,
        conversationId: sessionHistoryQuery.data.pages[0].conversationId ?? '',
        emailId: sessionHistoryQuery.data.pages[0].emailId ?? '',
      }
    : undefined;

  const handleViewMail = useCallback(
    async (itemId: string) => {
      try {
        await displayEmail(itemId);
      } catch (error) {
        toast.error(t('assistant.viewMail.failed'), {
          description: error instanceof Error ? error.message : t('assistant.chat.unexpectedError'),
        });
      }
    },
    [t],
  );

  const isExistingSessionHistoryLoading =
    !!urlSessionId && isHistoryLoading && allMessages.length === 0;

  const lastMsg = messages.at(-1);
  const streamingHasNoText =
    isStreaming &&
    !(
      lastMsg?.role === 'assistant' &&
      lastMsg.parts.some((p) => p.type === 'text' && (p as TextUIPart).text.trim().length > 0)
    );
  const showThinkingBanner = isLoading || streamingHasNoText;

  // What the assistant is doing this turn, derived from the live message's
  // tool-call parts. Present for as long as the live message is in memory, so
  // the trail stays with the answer after the turn ends (a reloaded session
  // shows history only, which carries no tool parts).
  const activeAssistantMsg = lastMsg?.role === 'assistant' ? lastMsg : undefined;
  const activitySteps = activeAssistantMsg ? extractActivitySteps(activeAssistantMsg) : [];
  const activityMessageId = activeAssistantMsg
    ? ((activeAssistantMsg.metadata as MessageMetadata | undefined)?.assistantMessageId ??
      activeAssistantMsg.id)
    : undefined;
  const currentStep = currentActivityStep(activitySteps);
  const currentStepLabel = currentStep
    ? t(`assistant.activity.steps.${currentStep.labelKey}`)
    : undefined;

  const displayMessages = showThinkingBanner
    ? allMessages.filter((m, i) => {
        if (i !== allMessages.length - 1) {
          return true;
        }
        return !(m.role === 'ASSISTANT' && !m.content?.trim());
      })
    : allMessages;

  return (
    <div
      className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden"
      ref={containerRef}
    >
      <EmailContextDialog
        activeSessionContext={activeSessionContext}
        contextEmailIds={contextEmailIds}
        onOpenChange={onContextOpenChange}
        open={showContextDialog}
        sessionId={urlSessionId ?? null}
      />

      {isExistingSessionHistoryLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <ThinkingIndicator />
        </div>
      ) : showWelcome ? (
        <div className="xs:gap-9 xs:px-6 xs:py-12 flex min-h-0 w-full flex-1 flex-col gap-6 overflow-hidden px-4 py-8">
          <Greeting userName={userInfo.name ?? ''} />
          <SmartSuggestions
            isVisible={showWelcome}
            onSelect={onSuggestionSelect}
            sessionId={urlSessionId}
          />
        </div>
      ) : (
        <ChatMessages
          // While the banner is up the empty assistant message is filtered out,
          // so the trail rides along with the banner in the footer; once text
          // starts streaming it renders above that message's bubble instead.
          // Either way the order on screen is banner → trail → answer.
          activityMessageId={showThinkingBanner ? undefined : activityMessageId}
          activitySteps={activitySteps}
          fetchNextPage={sessionHistoryQuery.fetchNextPage}
          footer={
            showThinkingBanner ? (
              <>
                <ThinkingBanner label={currentStepLabel} />
                <ActivityTrail steps={activitySteps} />
              </>
            ) : null
          }
          hasNextPage={sessionHistoryQuery.hasNextPage ?? false}
          isFetchingNextPage={sessionHistoryQuery.isFetchingNextPage ?? false}
          isLoading={isLoading}
          isStreaming={isStreaming && displayMessages.at(-1)?.role === 'ASSISTANT'}
          key={urlSessionId ?? 'new'}
          messages={displayMessages}
          onViewMail={handleViewMail}
        />
      )}
    </div>
  );
}
